import React, { useEffect, useState } from 'react';
import * as emotion from 'emotion';
import * as emotionCore from '@emotion/core';
import * as emotionTheming from 'emotion-theming';
import { getConsentFor, onConsentChange, } from '@guardian/consent-management-platform';
import { getZIndex } from '@root/src/web/lib/getZIndex';
import { submitComponentEvent, record, } from '@root/src/web/browser/ophan/ophan';
import { initPerf } from '@root/src/web/browser/initPerf';
const containerStyles = emotion.css `
    position: fixed;
    bottom: -1px;
    width: 100%;
    ${getZIndex('banner')}
`;
export const hasRequiredConsents = () => new Promise((resolve, reject) => {
    onConsentChange((state) => {
        try {
            resolve(getConsentFor('braze', state));
        }
        catch (e) {
            reject(e);
        }
    });
});
export const canShowPreChecks = ({ brazeSwitch, apiKey, isDigitalSubscriber, pageConfig, }) => Boolean(brazeSwitch &&
    apiKey &&
    isDigitalSubscriber &&
    !pageConfig.isPaidContent);
const getMessageFromBraze = async (apiKey, brazeUuid) => {
    const sdkLoadTiming = initPerf('braze-sdk-load');
    sdkLoadTiming.start();
    const { default: appboy } = await import(
    /* webpackChunkName: "braze-web-sdk-core" */ '@braze/web-sdk-core');
    const sdkLoadTimeTaken = sdkLoadTiming.end();
    record({
        component: 'braze-sdk-load-timing',
        value: sdkLoadTimeTaken,
    });
    const appboyTiming = initPerf('braze-appboy');
    appboyTiming.start();
    appboy.initialize(apiKey, {
        enableLogging: false,
        noCookies: true,
        baseUrl: 'https://sdk.fra-01.braze.eu/api/v3',
        sessionTimeoutInSeconds: 1,
        minimumIntervalBetweenTriggerActionsInSeconds: 0,
    });
    const canShowPromise = new Promise((resolve) => {
        appboy.subscribeToInAppMessage((message) => {
            const { extras } = message;
            const logButtonClickWithBraze = (internalButtonId) => {
                const thisButton = new appboy.InAppMessageButton(`Button: ID ${internalButtonId}`, undefined, undefined, undefined, undefined, undefined, internalButtonId);
                appboy.logInAppMessageButtonClick(thisButton, message);
            };
            const logImpressionWithBraze = () => {
                // Log the impression with Braze
                appboy.logInAppMessageImpression(message);
            };
            if (extras) {
                const meta = {
                    dataFromBraze: extras,
                    logImpressionWithBraze,
                    logButtonClickWithBraze,
                };
                resolve({ result: true, meta });
            }
            else {
                resolve({ result: false });
            }
        });
        appboy.changeUser(brazeUuid);
        appboy.openSession();
    });
    canShowPromise
        .then(() => {
        const appboyTimeTaken = appboyTiming.end();
        record({
            component: 'braze-appboy-timing',
            value: appboyTimeTaken,
        });
    })
        .catch(() => {
        appboyTiming.clear();
        // eslint-disable-next-line no-console
        console.log('Appboy Timing failed.');
    });
    return canShowPromise;
};
const getBrazeMetaFromQueryString = () => {
    if (URLSearchParams) {
        const params = new URLSearchParams(window.location.search);
        const qsArg = 'force-braze-message';
        const value = params.get(qsArg);
        if (value) {
            try {
                const dataFromBraze = JSON.parse(value);
                return {
                    dataFromBraze,
                    logImpressionWithBraze: () => { },
                    logButtonClickWithBraze: () => { },
                };
            }
            catch (e) {
                // Parsing failed. Log a message and fall through.
                // eslint-disable-next-line no-console
                console.log(`There was an error with ${qsArg}: `, e.message);
            }
        }
    }
    return null;
};
// We can show a Braze banner if:
// - The Braze switch is on
// - We have a Braze API key
// - The user is a digital subscriber
// - We're not on a Glabs paid content page
// - We've got a Braze UUID from the API, given a user's ID Creds
// - The user has given Consent via CCPA or TCFV2
// - The Braze websdk appboy initialisation does not throw an error
// - The Braze app Boy subscription to in app message returns meta info
// OR
// - The force-braze-message query string arg is passed
export const canShow = async (asyncBrazeUuid, isDigitalSubscriber) => {
    const bannerTiming = initPerf('braze-banner');
    bannerTiming.start();
    const forcedBrazeMeta = getBrazeMetaFromQueryString();
    if (forcedBrazeMeta) {
        return {
            result: true,
            meta: forcedBrazeMeta,
        };
    }
    const { brazeSwitch } = window.guardian.config.switches;
    const apiKey = window.guardian.config.page.brazeApiKey;
    if (!canShowPreChecks({
        brazeSwitch,
        apiKey,
        isDigitalSubscriber,
        pageConfig: window.guardian.config.page,
    })) {
        return { result: false };
    }
    const [brazeUuid, hasGivenConsent] = await Promise.all([
        asyncBrazeUuid,
        hasRequiredConsents(),
    ]);
    if (!(brazeUuid && hasGivenConsent)) {
        return { result: false };
    }
    try {
        const result = await getMessageFromBraze(apiKey, brazeUuid);
        const timeTaken = bannerTiming.end();
        record({
            component: 'braze-banner-timing',
            value: timeTaken,
        });
        return result;
    }
    catch (e) {
        bannerTiming.clear();
        return { result: false };
    }
};
const BrazeBannerWithSatisfiedDependencies = ({ BrazeComponent, meta, }) => {
    useEffect(() => {
        // Log the impression with Braze
        meta.logImpressionWithBraze();
        // Log VIEW event with Ophan
        submitComponentEvent({
            component: {
                componentType: 'RETENTION_ENGAGEMENT_BANNER',
                id: meta.dataFromBraze.componentName,
            },
            action: 'VIEW',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement("div", { className: containerStyles },
        React.createElement(BrazeComponent, { logButtonClickWithBraze: meta.logButtonClickWithBraze, submitComponentEvent: submitComponentEvent, componentName: meta.dataFromBraze.componentName, brazeMessageProps: meta.dataFromBraze })));
};
export const BrazeBanner = ({ meta }) => {
    const [BrazeComponent, setBrazeComponent] = useState();
    useEffect(() => {
        if (meta) {
            // TODO: unify the way we handle sharing these deps (this is
            // duplicated in SlotBodyEnd). Probably via the automat client
            // library.
            window.guardian.automat = {
                react: React,
                preact: React,
                emotionCore,
                emotionTheming,
                emotion,
            };
            import(
            /* webpackChunkName: "guardian-braze-components" */ '@guardian/braze-components')
                .then((module) => {
                setBrazeComponent(() => module.BrazeMessage);
            })
                .catch((error) => window.guardian.modules.sentry.reportError(error, 'braze-banner'));
        }
    }, [meta]);
    if (BrazeComponent && meta) {
        return (React.createElement(BrazeBannerWithSatisfiedDependencies, { BrazeComponent: BrazeComponent, meta: meta }));
    }
    return React.createElement("div", null);
};
